import { Injectable, OnDestroy } from '@angular/core';


export const TOOLTIP_DELAY_MS = 2000; // 2 second delay before auto-close
export const FOCUS_DELAY_MS = 150; // Small delay for focus management

/**
 * WCAG 2.2 Accessibility Helper Service
 * Provides reusable functions for common accessibility patterns
 * including tooltip/popover management, keyboard navigation, and ARIA support
 */
@Injectable({
  providedIn: 'root'
})
export class AccessibilityHelpersService implements OnDestroy {
  
  private tooltipTimeouts: Map<string, any> = new Map();
  private activePopovers: Map<string, any> = new Map(); // Track all open popovers for exclusivity
  private currentActivePopoverId: string | null = null; // Global tracking for single popover rule

  ngOnDestroy(): void {
    this.clearAllTooltipTimeouts();
    this.closeAllPopovers();
  }

  // WCAG 2.2 Compliance Helper Functions for Popovers/Tooltips
  
  /**
   * Enhanced popover management with exclusivity for repeated elements
   * WCAG 2.2: Focus Order (2.4.3) - Only one popover should be active at a time
   * Use this for components with multiple instances (like act 48 icons)
   */
  showPopoverExclusive(popover: any, instanceId: string): void {
    // Close any currently open popover
    this.closeCurrentActivePopover();
    
    if (popover && popover.open) {
      this.clearTooltipTimeout(instanceId);
      popover.open();
      
      // Track the active popover
      this.activePopovers.set(instanceId, popover);
      this.currentActivePopoverId = instanceId;
    }
  }
  
  /**
   * Closes the currently active popover and clears tracking
   * WCAG 2.2: Content on Hover or Focus (1.4.13)
   */
  closeCurrentActivePopover(): void {
    if (this.currentActivePopoverId) {
      const activePopover = this.activePopovers.get(this.currentActivePopoverId);
      if (activePopover && activePopover.close) {
        activePopover.close();
      }
      this.activePopovers.delete(this.currentActivePopoverId);
      this.clearTooltipTimeout(this.currentActivePopoverId);
      this.currentActivePopoverId = null;
    }
  }
  
  /**
   * Closes specific popover by instance ID
   * WCAG 2.2: Content on Hover or Focus (1.4.13)
   */
  closePopoverByInstance(instanceId: string): void {
    const popover = this.activePopovers.get(instanceId);
    if (popover && popover.close) {
      popover.close();
      this.activePopovers.delete(instanceId);
      this.clearTooltipTimeout(instanceId);
      
      if (this.currentActivePopoverId === instanceId) {
        this.currentActivePopoverId = null;
      }
    }
  }
  
  /**
   * Checks if a specific popover instance is currently active
   */
  isPopoverActive(instanceId: string): boolean {
    return this.currentActivePopoverId === instanceId;
  }
  
  /**
   * Generates unique popover ID for repeated elements
   * WCAG 2.2: Name, Role, Value (4.1.2)
   */
  generatePopoverId(componentPrefix: string, index: number): string {
    return `${componentPrefix}-popover-${index}-${Date.now()}`;
  }
  
  /**
   * Shows tooltip on hover with proper timing for accessibility
   * WCAG 2.2: Content on Hover or Focus (1.4.13)
   * Note: For ng-bootstrap tooltips, we let the library handle show/hide automatically
   */
  showTooltip(tooltip: any, instanceId?: string): void {
    if (tooltip && tooltip.open) {
      this.clearTooltipTimeout(instanceId);
      tooltip.open();
    }
  }

  /**
   * Shows tooltip with manual control (for custom implementations)
   */
  showTooltipManual(tooltip: any, instanceId?: string): void {
    if (tooltip && tooltip.open) {
      this.clearTooltipTimeout(instanceId);
      // Ensure tooltip stays open by clearing any pending close timeouts
      tooltip.open();
    }
  }

  /**
   * Hides tooltip after delay, allowing users time to interact
   * WCAG 2.2: Content on Hover or Focus (1.4.13) - Hoverable requirement
   */
  hideTooltipAfterDelay(tooltip: any, instanceId?: string): void {
    if (tooltip && tooltip.close) {
      this.clearTooltipTimeout(instanceId);
      const timeoutId = setTimeout(() => {
        if (tooltip && tooltip.close) {
          tooltip.close();
        }
        this.tooltipTimeouts.delete(instanceId || 'default');
      }, TOOLTIP_DELAY_MS);
      
      this.tooltipTimeouts.set(instanceId || 'default', timeoutId);
    }
  }

  /**
   * Immediately hides tooltip (for mouse leave events)
   * WCAG 2.2: Content on Hover or Focus (1.4.13)
   */
  hideTooltip(tooltip: any, instanceId?: string): void {
    if (tooltip && tooltip.close) {
      this.clearTooltipTimeout(instanceId);
      tooltip.close();
    }
  }

  /**
   * Simple approach: Just clear timeouts, let ng-bootstrap handle show/hide
   * Use this if the full manual control is causing conflicts
   */
  clearTooltipTimeoutsOnly(instanceId?: string): void {
    this.clearTooltipTimeout(instanceId);
  }

  /**
   * Handles keyboard interaction for tooltips
   * WCAG 2.2: Keyboard (2.1.1) and Focus Visible (2.4.7)
   */
  handleTooltipKeyDown(event: KeyboardEvent, tooltip: any, instanceId?: string): void {
  if (event.key === 'Escape') {     
      this.hideTooltip(tooltip, instanceId);
      // Return focus to the trigger element
      setTimeout(() => {
        (event.target as HTMLElement)?.focus();
      }, FOCUS_DELAY_MS);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (tooltip.isOpen && tooltip.isOpen()) {
        this.hideTooltip(tooltip, instanceId);
      } else {
        this.showTooltip(tooltip, instanceId);
      }
    }
  }

  /**
   * Handles focus events for keyboard users
   * WCAG 2.2: Content on Hover or Focus (1.4.13) - Focusable requirement
   */
  handleTooltipFocus(tooltip: any, instanceId?: string): void {
    // For ng-bootstrap tooltips, we can just clear any pending hide timeouts
    // The tooltip should show automatically on focus
    this.clearTooltipTimeout(instanceId);
  }

  /**
   * Handles blur events for keyboard users  
   * WCAG 2.2: Content on Hover or Focus (1.4.13)
   */
  handleTooltipBlur(tooltip: any, instanceId?: string): void {
    // For ng-bootstrap tooltips, let the library handle blur behavior
    // Just clear our custom timeouts
    this.clearTooltipTimeout(instanceId);
  }

  /**
   * Alternative focus handler that manually opens tooltip
   * Use this if automatic focus behavior isn't working
   */
  handleTooltipFocusManual(tooltip: any, instanceId?: string): void {
    this.showTooltipManual(tooltip, instanceId);
  }

  /**
   * Alternative blur handler with delay
   * Use this if you need custom blur timing
   */
  handleTooltipBlurWithDelay(tooltip: any, instanceId?: string): void {
    setTimeout(() => {
      if (tooltip && !this.isTooltipFocused(tooltip)) {
        this.hideTooltip(tooltip, instanceId);
      }
    }, FOCUS_DELAY_MS);
  }

  /**
   * Creates accessible status text for screen readers
   * WCAG 2.2: Info and Relationships (1.3.1)
   */
  createAccessibleStatusText(
    primaryStatus: string, 
    secondaryStatuses?: string[], 
    context?: string
  ): string {
    let statusText = primaryStatus;
    
    if (context) {
      statusText = `${context}: ${statusText}`;
    }
    
    if (secondaryStatuses && secondaryStatuses.length > 0) {
      statusText += `, ${secondaryStatuses.join(', ')}`;
    }
    
    return statusText;
  }

  /**
   * Gets human-readable status description for accessibility
   * Can be customized with status mappings for different domains
   * WCAG 2.2: Info and Relationships (1.3.1)
   */
  getStatusDescription(
    statusId: number | string | null | undefined, 
    statusMappings: { [key: string]: string }
  ): string {
    // Handle null, undefined, or empty values
    if (statusId === null || statusId === undefined || statusId === '') {
      return 'Status not available';
    }
    
    const mapping = statusMappings[statusId.toString()];
    return mapping || 'Status unknown';
  }

  /**
   * Determines appropriate ARIA role for different types of status indicators
   * WCAG 2.2: Name, Role, Value (4.1.2)
   */
  getAriaRole(type: 'status' | 'alert' | 'button' | 'img' = 'status'): string {
    return type;
  }

  /**
   * Provides appropriate ARIA live region setting based on urgency
   * WCAG 2.2: Status Messages (4.1.3)
   */
  getAriaLive(isUrgent: boolean = false): 'polite' | 'assertive' {
    return isUrgent ? 'assertive' : 'polite';
  }

  /**
   * Creates comprehensive ARIA attributes object for status indicators
   * WCAG 2.2: Multiple criteria support
   */
  createAriaAttributes(options: {
    label?: string;
    describedBy?: string;
    role?: string;
    live?: 'polite' | 'assertive';
    expanded?: boolean;
    hasPopup?: boolean;
  }): { [key: string]: string | boolean } {
    const attrs: { [key: string]: string | boolean } = {};
    
    if (options.label) attrs['aria-label'] = options.label;
    if (options.describedBy) attrs['aria-describedby'] = options.describedBy;
    if (options.role) attrs['role'] = options.role;
    if (options.live) attrs['aria-live'] = options.live;
    if (options.expanded !== undefined) attrs['aria-expanded'] = options.expanded;
    if (options.hasPopup) attrs['aria-haspopup'] = 'true';
    
    return attrs;
  }

  /**
   * Validates color contrast ratios for WCAG compliance
   * WCAG 2.2: Contrast (Minimum) (1.4.3) and Contrast (Enhanced) (1.4.6)
   */
  validateColorContrast(
    foreground: string, 
    background: string, 
    level: 'AA' | 'AAA' = 'AA',
    isLargeText: boolean = false
  ): { isValid: boolean; ratio: number; required: number } {
    const requiredRatio = this.getRequiredContrastRatio(level, isLargeText);
    const calculatedRatio = this.calculateContrastRatio(foreground, background);
    
    return {
      isValid: calculatedRatio >= requiredRatio,
      ratio: Math.round(calculatedRatio * 100) / 100, // Round to 2 decimal places
      required: requiredRatio
    };
  }
  
  /**
   * Calculates the actual contrast ratio between two colors
   * Based on WCAG guidelines: https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
   */
  private calculateContrastRatio(foreground: string, background: string): number {
    const fgLuminance = this.getRelativeLuminance(foreground);
    const bgLuminance = this.getRelativeLuminance(background);
    
    const lighter = Math.max(fgLuminance, bgLuminance);
    const darker = Math.min(fgLuminance, bgLuminance);
    
    return (lighter + 0.05) / (darker + 0.05);
  }
  
  /**
   * Calculates relative luminance of a color
   * Based on WCAG formula
   */
  private getRelativeLuminance(color: string): number {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 0;
    
    // Convert to sRGB
    const rsRGB = rgb.r / 255;
    const gsRGB = rgb.g / 255;
    const bsRGB = rgb.b / 255;
    
    // Apply gamma correction
    const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
    
    // Calculate luminance
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  
  /**
   * Converts hex color to RGB
   */
  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle 3-digit hex
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    if (hex.length !== 6) return null;
    
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Manages focus trapping for modal dialogs and overlays
   * WCAG 2.2: Focus (2.4.3) and Focus Order (2.4.3)
   */
  trapFocus(container: HTMLElement): () => void {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    firstElement?.focus();

    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }

  /**
   * Announces content to screen readers without visual disruption
   * WCAG 2.2: Status Messages (4.1.3)
   */
  announceToScreenReader(
    message: string, 
    priority: 'polite' | 'assertive' = 'polite'
  ): void {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.setAttribute('class', 'sr-only');
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  

  // Private helper methods

  /**
   * Clears timeout for a specific tooltip instance
   */
  private clearTooltipTimeout(instanceId?: string): void {
    const id = instanceId || 'default';
    const timeoutId = this.tooltipTimeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.tooltipTimeouts.delete(id);
    }
  }

  /**
   * Clears all tooltip timeouts (for cleanup)
   */
  private clearAllTooltipTimeouts(): void {
    this.tooltipTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.tooltipTimeouts.clear();
  }
  
  /**
   * Closes all active popovers (for cleanup)
   */
  private closeAllPopovers(): void {
    this.activePopovers.forEach((popover) => {
      if (popover && popover.close) {
        popover.close();
      }
    });
    this.activePopovers.clear();
    this.currentActivePopoverId = null;
  }

  /**
   * Simplified approach for ng-bootstrap tooltips - minimal interference
   * Just handles keyboard events, lets ng-bootstrap handle mouse events
   */
  handleTooltipKeyboardOnly(event: KeyboardEvent, tooltip: any, instanceId?: string): void {
    if (event.key === 'Escape') {
      if (tooltip && tooltip.close) {
        tooltip.close();
      }
      // Return focus to the trigger element
      setTimeout(() => {
        (event.target as HTMLElement)?.focus();
      }, FOCUS_DELAY_MS);
    }
    // Don't handle Enter/Space as ng-bootstrap may conflict
  }

  /**
   * Even simpler approach - just handle Escape key
   * Use this if you're having conflicts with other keyboard handling
   */
  handleEscapeKey(event: KeyboardEvent, tooltip: any): void {
    if (event.key === 'Escape' && tooltip && tooltip.close) {
      tooltip.close();
      (event.target as HTMLElement)?.focus();
    }
  }

  /**
   * Checks if tooltip content currently has focus
   */
  private isTooltipFocused(tooltip: any): boolean {
    // This would need to be implemented based on the specific tooltip library
    // For ng-bootstrap tooltips, this is typically not needed as they handle focus internally
    return false;
  }

  /**
   * Gets required contrast ratio based on WCAG level and text size
   */
  private getRequiredContrastRatio(level: 'AA' | 'AAA', isLargeText: boolean): number {
    if (level === 'AAA') {
      return isLargeText ? 4.5 : 7;
    }
    return isLargeText ? 3 : 4.5;
  }
}